import { ColDef, GridApi, ValueFormatterParams } from '@ag-grid-community/core'
import { Store } from '@ngrx/store'
import { filter, take } from 'rxjs/operators'
import { CommentaryEntityContext } from '@helios/common-api'
import {
  openCommentaryDrawer,
  RootState,
  selectUserPolicy,
  UserPolicy,
  userHasCommentaryAccess,
} from '@helios/state-management'
import { commentCountRendererKey } from './comment-count-cell-renderer'
import { CommentaryCountsService } from '../services/commentary-counts.service'
import { CommentaryCountDimension } from '../types/commentary-count-dimension'
import { CommentaryRowContext } from '../types/commentary-row-context'

/**
 * colId of the pinned "Comments" count column. It is a UI-only column (comment
 * counts, no backing data field), so grids MUST exclude it from server-side
 * Excel-export requests — the export endpoint 400s on an unknown `comments`
 * field. Single source of truth so the column and the export filters agree.
 */
export const COMMENTARY_COLUMN_ID = 'comments'

// The shared set-filter converter turns exactly these into the booleans the
// backend's `comments` filter takes.
export const HAS_COMMENTS = 'True'
export const NO_COMMENTS = 'False'

export function isCommentsFilterActive(api: GridApi): boolean {
  return api.getFilterModel()?.[COMMENTARY_COLUMN_ID] != null
}

/**
 * DLITE-5094: the Comments column's value for a row — its count, or `null`
 * when the user cannot read the row's account's commentary. Shared by the
 * column and the client-side refilter so both classify a row the same way.
 */
export function commentaryCountValue(
  countsService: CommentaryCountsService,
  row: CommentaryEntityContext | null | undefined,
  countDimension: CommentaryCountDimension,
): number | null {
  if (!row) return 0
  if (countsService.isUnavailable(row.accountId)) return null
  return countsService.countForRow(row, countDimension)
}

/** True when the row counts as HAS_COMMENTS (an unavailable row never does). */
export function rowHasComments(
  countsService: CommentaryCountsService,
  row: CommentaryEntityContext | null | undefined,
  countDimension: CommentaryCountDimension,
): boolean {
  return (commentaryCountValue(countsService, row, countDimension) ?? 0) > 0
}

/**
 * Returns the ColDef for the pinned "Comments" count column.
 * The grid must subscribe to `countsService.changes$` and call
 * `gridApi.refreshCells({ columns: [COMMENTARY_COLUMN_ID] })` to keep cells in sync.
 *
 * `countDimension` is the dimension the badge counts; `commentary` supplies
 * every dimension a thread created from the row links to. The two are separate
 * because they answer different questions — a break row links its transaction
 * and asset as well as itself, but its badge counts only the break's threads.
 */
export function getCommentaryColumn<T>(
  store: Store,
  commentary: CommentaryRowContext<T>,
  hasAccessCommentary: () => boolean,
  countsService: CommentaryCountsService,
  countDimension: CommentaryCountDimension,
): ColDef {
  // DLITE-5094: `null` marks a row whose account's commentary the user cannot
  // read; the renderer draws it as unavailable rather than as "0 comments".
  const valueOf = (data: T | undefined): number | null =>
    data
      ? commentaryCountValue(
          countsService,
          commentary.build(data),
          countDimension,
        )
      : 0
  const countOf = (data: T | undefined): number => valueOf(data) ?? 0
  return {
    headerName: 'Comments',
    colId: COMMENTARY_COLUMN_ID,
    width: 80,
    pinned: 'left' as const,
    sortable: false,
    filter: 'agSetColumnFilter',
    // Server-side grids send the model to the backend; client-side grids
    // filter on the loaded counts through filterValueGetter.
    filterParams: {
      values: [HAS_COMMENTS, NO_COMMENTS],
      valueFormatter: (params: ValueFormatterParams) =>
        params.value === HAS_COMMENTS ? 'Has comments' : 'No comments',
      suppressMiniFilter: true,
      suppressSorting: true,
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      defaultToNothingSelected: true,
    },
    // An unavailable row counts as 0 here, i.e. NO_COMMENTS.
    filterValueGetter: params =>
      countOf(params.data) > 0 ? HAS_COMMENTS : NO_COMMENTS,
    cellRenderer: commentCountRendererKey,
    valueGetter: params => valueOf(params.data),
    onCellClicked: params => {
      if (!hasAccessCommentary() || !params.data) return
      store.dispatch(
        openCommentaryDrawer({
          entityContext: commentary.build(params.data),
          commentaryAction: 'view',
        }),
      )
    },
  }
}

/**
 * Awaits the first defined user policy in the store and returns either
 * `[getCommentaryColumn(...)]` or `[]` based on whether the user is
 * permitted to see commentary. Callers spread this into their
 * `baseColDefs`, so users without access never see an empty "Comments"
 * column header taking up grid space.
 *
 * The policy is the source of truth here (not the component's
 * `hasAccessCommentary` field, which is set asynchronously and is `false`
 * at the time `createBaseColumnDefs` runs as a class-property initializer
 * chain). The `hasAccessCommentaryGetter` closure is still used for the
 * click handler so it tracks any subsequent policy changes.
 */
export async function getCommentaryColumnIfAccessible<T>(
  store: Store<RootState>,
  commentary: CommentaryRowContext<T>,
  hasAccessCommentaryGetter: () => boolean,
  countsService: CommentaryCountsService,
  countDimension: CommentaryCountDimension,
): Promise<ColDef[]> {
  const policy = await store
    .select(selectUserPolicy)
    .pipe(
      filter((p): p is UserPolicy => !!p),
      take(1),
    )
    .toPromise()
  if (!userHasCommentaryAccess(policy)) return []
  return [
    getCommentaryColumn(
      store,
      commentary,
      hasAccessCommentaryGetter,
      countsService,
      countDimension,
    ),
  ]
}
